import { AppBar, Card, CardContent, FormControl, FormGroup, FormHelperText, InputLabel, ListItemText, MenuItem, Select, Tab, Tabs, TextField, Toolbar, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import { SquarePicture } from "assets/appConfig";
import { AppImages } from "assets/appResources";
import { FormStyleClasses, GlobalClassKeys, TabLargeStyle } from "assets/jss/common-styles";
import ButtonBack from "components/Buttons/ButtonBack";
import ButtonSave from "components/Buttons/ButtonSave";
import { RequestIndicator } from "components/CallServer";
import RequestErrorDialog from "components/CallServer/RequestErrorDialog";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import { ImageSelection } from "components/FileUpload/ImageSelection";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiContainer from "components/MuiExtras/MuiContainer";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import { Formaters } from "lib/Formarters";
import LibUtils from "lib/LibUtils";
import MediaUtils from "lib/MediaUtils";
import { AccountType } from "models/ApiEnums";
import { GetUserAccountDto, SaveAccountDto, UserPhoneModel, UserRecordFormConfigDto } from "models/ApiModels";
import { RequestLifeCycle } from "models/AppModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { RouteComponentProps } from 'react-router-dom';
import { RoutePaths } from "routes/RouteConfig";
import Session from "services/Session";
import LocationAutoComplete from "views/User/Components/LocationAutoComplete";
import { RenderAccountTopHeaderInfo } from "views/User/Components/UserEdit";
import UserEditBannedPeople from "views/User/Components/UserEditBannedPeople";
import UserEditCredentials from "views/User/Components/UserEditCredentials";
import UserEditFollowAuthors from "views/User/Components/UserEditFollowAuthors";
import UserEditFollowTopics from "views/User/Components/UserEditFollowTopics";
import UserEditKnowsAbout from "views/User/Components/UserEditKnowsAbout";
import UserEditPhones from "views/User/Components/UserEditPhones";
import UserEditSocialNetworks from "views/User/Components/UserEditSocialNetworks";

/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
}

interface IState {
    formCycle: RequestLifeCycle,
    lastError: any,
    dto: GetUserAccountDto,
    currentTab: TabOptions,
    validate: {
        name: boolean,
        email: boolean,
        timezoneId: boolean,
        culture: boolean,
        phones: boolean;
        location: boolean,
        thumbnail: boolean;
        nameUnique: boolean;
        emailUnique: boolean;
    };
}

type Props = IProps & RouteComponentProps<IProps> & WithStyles<typeof FormStyleClasses> & WithSnackbar & WithTranslation;

enum TabOptions { About, Following }

/////////////////////////////////////////////////////////////////////////////////////

class AccountEdit extends React.Component<Props, IState> {

    private settings: UserRecordFormConfigDto;
    private loadDto: any = null;

    ///
    constructor(props: Props) {
        super(props);

        this.loadDto = null;

        this.state = {
            formCycle: RequestLifeCycle.Init,
            lastError: null,
            validate: {
                name: null,
                email: null,
                timezoneId: null,
                culture: null,
                phones: null,
                location: null,
                thumbnail: true,
                nameUnique: null,
                emailUnique: null
            },
            dto: null,
            currentTab: TabOptions.About
        };
    }

    ///
    private res(r: string) {
        return this.props.t("Views.User.UserEdit." + r);
    }

    ///
    private initialLoad() {

        Promise.all([api.getUserFormEditOptions(Session.user.culture), api.getAccount()])
            .then(([options, dto]) => {
                this.settings = options;

                this.loadDto = LibUtils.deepCopy(dto);

                this.setState({
                    formCycle: RequestLifeCycle.InitSuccess,
                    dto: dto,
                    validate: {
                        culture: dto.culture.length > 0,
                        email: dto.email.length > 0,
                        emailUnique: true,

                        location: !this.settings.editLocalInUserProfile
                            ? true
                            : this.settings.chooseLocalFromList
                                ? dto.locationId != null && dto.locationId.length > 0
                                : dto.locationName != null && dto.locationName.length > 0,

                        name: dto.name.length > 0,
                        nameUnique: true,
                        phones: true,
                        timezoneId: dto.timezoneId.length > 0,
                        thumbnail: true
                    }
                });
            })
            .catch((error: any) => {
                this.setState({
                    formCycle: RequestLifeCycle.InitFailed,
                    lastError: error
                });
            });
    }

    ///
    private isRecordChanged() {
        return !LibUtils.deepCompare(this.state.dto, this.loadDto);
    }

    ///
    private isValidForm(): boolean {
        var result = true;
        Object
            .getOwnPropertyNames(this.state.validate)
            .forEach((v) => {
                result = result && this.state.validate[v];
            });
        return result;
    }

    ///
    private getPostDto() {
        const { dto } = this.state;
        const postDto: SaveAccountDto = {} as SaveAccountDto;

        postDto.userId = dto.userId;
        postDto.name = dto.name;
        postDto.email = dto.email;
        postDto.timezoneId = dto.timezoneId;
        postDto.culture = dto.culture;
        postDto.thumbnail = dto.thumbnail == null ? null : { fileId: dto.thumbnail.fileId };

        postDto.locationId = dto.locationId;
        postDto.locationName = dto.locationName;

        postDto.personal = dto.personal;
        postDto.followUsers = dto.followingUsers.map(v => v.userId);
        postDto.followTopics = dto.followingTopics.map(v => v.topicId);
        postDto.knowsAbout = dto.knowsAbout;
        postDto.bannedPeople = dto.bannedPeople.map(v => v.blockedUser.id);

        return postDto;
    }

    ///
    private handleTabSelection = (event: React.ChangeEvent<{}>, value: any) => {
        this.setState({ currentTab: value });
    };

    ///
    private handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        this.updateRecord(
            event.target.name,
            event.target.type == "checkbox"
                ? (event.target as HTMLInputElement).checked
                : (event.target as HTMLInputElement).valueAsNumber
                    ? (event.target as HTMLInputElement).valueAsNumber
                    : event.target.value
        );
    };

    ///
    private updateRecord(name: string, v: any) {

        this.setState(state => {
            switch (name) {
                case "name":
                    state.dto.name = v;
                    state.validate.name = v != "";
                    break;

                case "email":
                    state.dto.email = v;
                    state.validate.email = v != "";
                    break;

                case "ddlTimezoneId":
                    state.dto.timezoneId = v;
                    state.validate.timezoneId = v != "";
                    break;

                case "ddlCulture":
                    state.dto.culture = v;
                    state.validate.culture = v != "";
                    break;

                case "thumbnail":
                    if (v == null) state.dto.thumbnail = null;
                    else state.dto.thumbnail = { fileId: v.fileId, url: v.tempUrl };
                    break;

                case "thumbnailUploading":
                    state.validate.thumbnail = !v;
                    break;

                case "location":
                    state.dto.locationId = v == null ? null : v.key;
                    state.dto.locationName = v == null ? null : v.label;
                    state.validate.location = v != null && v.key != "";
                    break;

                case "locationName":
                    state.dto.locationName = v;
                    state.validate.location = v != "";
                    break;

                case "phones":
                    state.dto.personal.phones = v;
                    state.validate.phones = (v as UserPhoneModel[]).findIndex(p => p.isValid === false) == -1;
                    break;

                case "networks":
                    state.dto.personal.socialNetworks = v;
                    break;

                case "credentials":
                    state.dto.personal.credentials = v;
                    break;

                case "knowsAbout":
                    state.dto.knowsAbout = v;
                    break;

                case "followTopics":
                    state.dto.followingTopics = v;
                    break;

                case "followAuthors":
                    state.dto.followingUsers = v;
                    break;

                case "bannedPeople":
                    state.dto.bannedPeople = v;
                    break;
            }
            return state;
        });
    }

    ///
    private handleSubmit(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        const validated = true;

        if (validated) {

            const dto = this.getPostDto();

            this.setState({
                formCycle: RequestLifeCycle.Posting,
            });

            api.updateAccount(dto)
                .then((newDto) => {
                    Session.updateUserInfo(newDto);
                    this.loadDto = JSON.stringify(newDto);
                    this.props.snackbar.showMessage(this.res("UpdateProfileSuccess"), "success");
                    this.props.history.replace(RoutePaths.TimelineStartup);
                })
                .catch((error: any) => {
                    this.setState({ lastError: error, formCycle: RequestLifeCycle.PostFailed });
                });
        }
    }

    ///
    private checkEmail = () => {
        api.checkUserEmailIsUnique(this.state.dto.userId, this.state.dto.email)
            .then((result) => {
                this.setState(state => {
                    state.validate.emailUnique = result;
                    return state;
                });
            });
    };

    ///
    private checkUsername = () => {
        api.checkUserNameIsUnique(this.state.dto.userId, this.state.dto.name)
            .then((result) => {
                this.setState(state => {
                    state.validate.nameUnique = result;
                    return state;
                });
            });
    };

    ///
    private renderFormPhones() {
        return (
            <MuiContainer margin="normal">

                <UserEditPhones
                    phones={this.state.dto.personal.phones}
                    editResidentialPhones={Session.user.id == this.state.dto.userId}
                    onChange={(phones) => this.updateRecord("phones", phones)}
                    message={
                        <MuiMessageBox
                            hideIcon={true}
                            size="normal"
                            variant="notice"
                            message={
                                <ul style={{ listStyle: "square" }}>
                                    <li>{this.res("PhonesL1")}</li>
                                    <li>{this.res("PhonesL2")}</li>
                                </ul>
                            }
                        />
                    }
                />

            </MuiContainer >
        );
    }

    ///
    private renderFormSocialNetworks() {
        return (
            <MuiContainer margin="normal">

                <UserEditSocialNetworks
                    networks={this.state.dto.personal.socialNetworks}
                    onChange={(networks) => this.updateRecord("networks", networks)}
                />

            </MuiContainer >
        );
    }

    ///
    private renderFormCredentials() {
        return (
            <MuiContainer margin="normal">

                <UserEditCredentials
                    credentials={this.state.dto.personal.credentials}
                    onChange={(credentials) => this.updateRecord("credentials", credentials)}
                />

            </MuiContainer >
        );
    }

    ///
    private renderFormKnowsAbout() {
        return (
            <MuiContainer margin="normal">

                <UserEditKnowsAbout
                    readonly={false}
                    userId={this.state.dto.userId}
                    userName={this.state.dto.name}
                    knowsAbout={this.state.dto.knowsAbout}
                    onChange={(knowsAbout) => this.updateRecord("knowsAbout", knowsAbout)}
                />

            </MuiContainer >
        );
    }

    ///
    private renderBannedPeople() {
        return this.state.dto.bannedPeople.length == 0
            ? null
            : (
                <MuiContainer margin="normal">

                    <UserEditBannedPeople
                        people={this.state.dto.bannedPeople}
                        onChange={(people) => this.updateRecord("bannedPeople", people)}
                    />

                </MuiContainer >
            );
    }


    ///
    private renderFollowTopics() {
        return (
            <MuiContainer margin="normal">

                <UserEditFollowTopics
                    follow={this.state.dto.followingTopics}
                    onChange={(follow) => this.updateRecord("followTopics", follow)}
                />

            </MuiContainer >
        );
    }

    ///
    private renderFollowAuthors() {
        return (
            <MuiContainer margin="normal">

                <UserEditFollowAuthors
                    follow={this.state.dto.followingUsers}
                    onChange={(follow) => this.updateRecord("followAuthors", follow)}
                />

            </MuiContainer >
        );
    }

    ///
    private renderForm() {
        const { dto, validate } = this.state;

        return (

            <MuiContainer margin="normal">

                <Card>

                    <CardContent>

                        <Grid container spacing={2}>
                            <Grid item>
                                <ImageSelection
                                    acceptedFiles={MediaUtils.AcceptImages()}
                                    allowEditSelectedImage={true}
                                    currentImageUrl={dto && dto.thumbnail ? dto.thumbnail.url : null}
                                    dimensions={SquarePicture}
                                    initialSource="local"
                                    emptyImageUrl={AppImages.DefaultUserThumbnail}
                                    enableClear={true}
                                    onClear={() => { this.updateRecord("thumbnail", null); }}
                                    onFileUploadStarted={() => this.updateRecord("thumbnailUploading", true)}
                                    onFileUploadFinished={() => this.updateRecord("thumbnailUploading", false)}
                                    onFileUploadSuccess={(data) => this.updateRecord("thumbnail", data.thumbnail ? data.thumbnail : data.mainFile)}
                                />

                                <FormGroup>
                                    {
                                        dto.account.accountExpirationDate != null &&
                                        <Typography variant="body1">
                                            <small>
                                                <b>{this.res("ExpiresIn")}</b>
                                                <br />
                                                {Formaters.localeDate(dto.account.accountExpirationDate)}
                                            </small>
                                        </Typography>
                                    }
                                </FormGroup>

                            </Grid>

                            <Grid item xs>

                                <FormGroup>
                                    <TextField
                                        name="name"
                                        label={this.props.t<string>("Common.Literals.NameLabel")}
                                        error={validate.name === false}
                                        value={dto == null ? "" : dto.name}
                                        onChange={this.handleChange}
                                        onBlur={this.checkUsername}
                                        margin="normal"
                                        fullWidth
                                        inputProps={{
                                            maxLength: 35,
                                        }}
                                    />
                                    {validate.nameUnique === false && <FormHelperText error>{this.props.t("Views.Public.Register.NameAlreadyExists")}</FormHelperText>}
                                </FormGroup>

                                <FormGroup>
                                    <TextField
                                        name="email"
                                        label={this.props.t<string>("Common.Literals.EmailLabel")}
                                        error={validate.email === false}
                                        value={dto == null ? "" : dto.email}
                                        onChange={this.handleChange}
                                        onBlur={this.checkEmail}
                                        disabled={this.state.dto.account?.accountType != AccountType.Local}
                                        margin="normal"
                                        fullWidth
                                        inputProps={{
                                            maxLength: 255,
                                        }}
                                    />
                                    {validate.emailUnique === false && <FormHelperText error>{this.props.t("Views.Public.Register.EmailAlreadyExists")}</FormHelperText>}
                                </FormGroup>


                                {
                                    this.settings.editLocalInUserProfile &&
                                    <FormGroup>
                                        {
                                            this.settings.chooseLocalFromList
                                                ? (
                                                    <LocationAutoComplete
                                                        label={this.settings.localName}
                                                        emptySelectionLabel={this.res("LocationEmptySelectionLabel")}
                                                        required={true}
                                                        margin="normal"
                                                        error={validate.location === false}
                                                        selectedItems={dto.locationId == null ? null : [{ key: dto.locationId, label: dto.locationName, canDelete: true }]}
                                                        onChange={(data) => { this.updateRecord("location", data.length == 0 ? null : data[0]); }}
                                                    />
                                                )
                                                : (
                                                    <TextField
                                                        name="locationName"
                                                        label={this.settings.localName}
                                                        error={validate.location === false}
                                                        value={dto.locationName || ""}
                                                        margin="normal"
                                                        placeholder={this.res("LocationNamePlaceholder")}
                                                        onChange={this.handleChange}
                                                        autoComplete="nope"
                                                        autoCorrect="off"
                                                        required
                                                        fullWidth
                                                        inputProps={{
                                                            maxLength: 255,
                                                        }}
                                                    />
                                                )
                                        }
                                    </FormGroup>

                                }


                                <Grid container spacing={2} >
                                    {
                                        false &&
                                        <Grid item xs={6}>
                                            <FormControl fullWidth margin="normal">
                                                <InputLabel htmlFor="LabelTimezone">{this.props.t("Common.Literals.TimezoneLabel")}</InputLabel>
                                                <Select
                                                    name="ddlTimezoneId"
                                                    error={validate.timezoneId === false}
                                                    value={dto == null ? "" : dto.timezoneId}
                                                    onChange={(e) => this.handleChange(e as React.ChangeEvent<HTMLInputElement>)}
                                                    inputProps={{ name: 'ddlTimezoneId', id: 'LabelTimezone' }}
                                                    autoWidth
                                                >
                                                    {this.settings.timezones.map((v) => (
                                                        <MenuItem key={v.zoneId} value={v.zoneId} dense >
                                                            <ListItemText primary={`${v.zoneId} (${v.name})`} />
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    }

                                    {
                                        false &&
                                        <Grid item xs={12}>
                                            <FormControl fullWidth margin="normal">
                                                <InputLabel htmlFor="ddlCulture">{this.props.t("Common.Literals.CultureLabel")}</InputLabel>
                                                <Select
                                                    error={validate.culture === false}
                                                    value={dto == null ? "" : dto.culture}
                                                    onChange={(e) => this.handleChange(e as React.ChangeEvent<HTMLInputElement>)}
                                                    inputProps={{ name: 'ddlCulture', id: 'ddlCulture' }}
                                                    autoWidth
                                                >
                                                    {this.settings.cultures.map((v) => (
                                                        <MenuItem key={v.key} value={v.key} dense>
                                                            <ListItemText primary={v.value} />
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    }
                                </Grid>

                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

            </MuiContainer>
        );
    }

    ///
    private renderToolbar() {
        var unsaveMessage = this.props.t("Common.Question.Unsave");

        return (
            <AppBar position="sticky" color="default" className={GlobalClassKeys.stickyToolbar}>
                <Toolbar>

                    <div className={GlobalClassKeys.grow}>
                        <ButtonBack
                            onClick={() => {
                                if (this.isRecordChanged()) {
                                    if (window.confirm(unsaveMessage)) {
                                        this.props.history.replace(RoutePaths.TimelineStartup);
                                    }
                                }
                                else {
                                    this.props.history.replace(RoutePaths.TimelineStartup);
                                }
                            }}
                        />
                    </div>

                    <ButtonSave
                        disabled={!this.isValidForm()}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => this.handleSubmit(e)}
                    />
                </Toolbar>
            </AppBar>
        );
    }

    ///
    private renderFormMenuBar(): JSX.Element {
        return (
            <MuiContainer margin="dense">
                <AppBar position="static" color="secondary"  >
                    <Tabs centered value={this.state.currentTab} onChange={this.handleTabSelection}>
                        <Tab value={TabOptions.About} label={this.res("TabAboutLabel")} style={TabLargeStyle} />
                        <Tab value={TabOptions.Following} label={this.res("TabFollowingLabel")} style={TabLargeStyle} />
                    </Tabs>
                </AppBar>
            </MuiContainer>
        );
    }

    ///
    componentDidMount() {
        this.initialLoad();
    }

    ///
    render() {
        if (this.state.formCycle == RequestLifeCycle.Init) {
            return <RequestIndicator />;
        }

        if (this.state.formCycle == RequestLifeCycle.InitFailed) {
            return <RequestErrorReport error={this.state.lastError} />;
        }

        return (
            <>
                {this.state.formCycle == RequestLifeCycle.Posting && <RequestIndicator type="post" />}

                <RequestErrorDialog
                    open={this.state.formCycle == RequestLifeCycle.PostFailed}
                    error={this.state.lastError}
                    onClose={() => this.setState({ lastError: null, formCycle: RequestLifeCycle.None })}
                />

                <Grid container justifyContent="space-around">
                    <Grid item xs={12} md={10} lg={8}>
                        <form noValidate autoComplete="off" >

                            {this.renderToolbar()}

                            <RenderAccountTopHeaderInfo accountType={this.state.dto?.account?.accountType} />

                            {this.renderForm()}

                            {this.renderFormMenuBar()}

                            {
                                this.state.currentTab == TabOptions.About
                                    ? (
                                        <>
                                            {this.renderFormPhones()}
                                            {this.renderFormCredentials()}
                                            {this.renderFormKnowsAbout()}
                                            {this.renderFormSocialNetworks()}
                                        </>
                                    )
                                    : (
                                        <>
                                            {this.renderFollowTopics()}
                                            {this.renderFollowAuthors()}
                                            {this.renderBannedPeople()}
                                        </>
                                    )
                            }
                        </form >
                    </Grid>
                </Grid>
            </>
        );
    }
}

export default withSnackbar(withTranslation()(withStyles(FormStyleClasses)(AccountEdit)));